import { createClient } from '@/lib/supabase/client';

export type ReactionType = 'insightful' | 'needs_citation' | 'eye_opening';
export type ContentType = 'contribution' | 'forum_post';

export interface ReactionCounts {
  insightful: number;
  needs_citation: number;
  eye_opening: number;
}

export interface UserReactions {
  insightful: boolean;
  needs_citation: boolean;
  eye_opening: boolean;
}

export const REACTION_META: Record<ReactionType, { label: string; emoji: string; color: string; bg: string }> = {
  insightful:      { label: 'Insightful',      emoji: '💡', color: '#E8C86A', bg: '#E8C86A18' },
  needs_citation:  { label: 'Needs Citation',  emoji: '📚', color: '#E05A47', bg: '#E05A4718' },
  eye_opening:     { label: 'Eye-opening',     emoji: '👁️', color: '#5B9E6B', bg: '#5B9E6B18' },
};

export async function getReactionCounts(
  contentType: ContentType,
  contentId: string
): Promise<ReactionCounts> {
  const supabase = createClient();
  const { data } = await supabase
    .from('reactions')
    .select('reaction_type')
    .eq('content_type', contentType)
    .eq('content_id', contentId);

  const counts: ReactionCounts = { insightful: 0, needs_citation: 0, eye_opening: 0 };
  for (const row of data || []) {
    if (row.reaction_type in counts) {
      counts[row.reaction_type as ReactionType]++;
    }
  }
  return counts;
}

export async function getUserReactions(
  contentType: ContentType,
  contentId: string,
  userId: string
): Promise<UserReactions> {
  const supabase = createClient();
  const { data } = await supabase
    .from('reactions')
    .select('reaction_type')
    .eq('content_type', contentType)
    .eq('content_id', contentId)
    .eq('user_id', userId);

  const result: UserReactions = { insightful: false, needs_citation: false, eye_opening: false };
  for (const row of data || []) {
    if (row.reaction_type in result) {
      result[row.reaction_type as ReactionType] = true;
    }
  }
  return result;
}

export async function toggleReaction(
  contentType: ContentType,
  contentId: string,
  userId: string,
  reactionType: ReactionType
): Promise<boolean> {
  const supabase = createClient();

  // Check if reaction exists
  const { data: existing } = await supabase
    .from('reactions')
    .select('id')
    .eq('content_type', contentType)
    .eq('content_id', contentId)
    .eq('user_id', userId)
    .eq('reaction_type', reactionType)
    .maybeSingle();

  if (existing) {
    await supabase.from('reactions').delete().eq('id', existing.id);
    return false; // removed
  } else {
    await supabase.from('reactions').insert({
      user_id: userId,
      content_type: contentType,
      content_id: contentId,
      reaction_type: reactionType,
    });
    return true; // added
  }
}

export async function getBulkReactionCounts(
  contentType: ContentType,
  contentIds: string[]
): Promise<Record<string, ReactionCounts>> {
  if (contentIds.length === 0) return {};
  const supabase = createClient();
  const { data } = await supabase
    .from('reactions')
    .select('content_id, reaction_type')
    .eq('content_type', contentType)
    .in('content_id', contentIds);

  const result: Record<string, ReactionCounts> = {};
  for (const id of contentIds) {
    result[id] = { insightful: 0, needs_citation: 0, eye_opening: 0 };
  }
  for (const row of data || []) {
    if (result[row.content_id] && row.reaction_type in result[row.content_id]) {
      result[row.content_id][row.reaction_type as ReactionType]++;
    }
  }
  return result;
}
